import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";

import { Can } from "../Can";
import {
	Grid,
	Paper,
	Tab,
	Tabs,
	Switch,
	Typography,
	withStyles
} from "@material-ui/core";
import { getBackendUrl } from "../../config";
import TabPanel from "../TabPanel";
import AvatarUploader from "../AvatarUpload";
import SchedulesForm from "../SchedulesForm";

const backendUrl = getBackendUrl();

const GreenSwitch = withStyles(() => ({
	root: {
		width: 36,
		height: 20,
		padding: 0,
		display: "flex"
	},
	switchBase: {
		padding: 2,
		color: "#fff",
		"&$checked": {
			transform: "translateX(16px)",
			color: "#fff",
			"& + $track": {
				opacity: 1,
				backgroundColor: "#4caf50",
				borderColor: "#4caf50"
			}
		}
	},
	thumb: {
		width: 16,
		height: 16,
		boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
	},
	track: {
		borderRadius: 10,
		opacity: 1,
		backgroundColor: "#bdbdbd"
	},
	checked: {}
}))(Switch);

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap"
	},
	dialogPaper: {
		minHeight: "72vh",
		maxHeight: "88vh"
	},
	mainPaper: {
		borderRadius: 14,
		overflow: "hidden",
		margin: theme.spacing(0, 3, 1)
	},
	paper: {
		margin: theme.spacing(0, 3, 2),
		background: "transparent"
	},
	tab: {
		padding: theme.spacing(0, 1)
	},
	dialogContent: {
		padding: theme.spacing(2)
	},
	btnWrapper: {
		position: "relative"
	},
	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1
	},
	container: {
		display: "flex",
		flexWrap: "wrap"
	},
	updateDiv: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center"
	},
	sectionCard: {
		padding: theme.spacing(2),
		borderRadius: 14,
		border: `1px solid ${
			theme.mode === "dark" ? "rgba(255,255,255,0.14)" : "rgba(15,23,42,0.08)"
		}`,
		background: theme.mode === "dark"
			? "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))"
			: "linear-gradient(180deg, #ffffff, #f8fafc)",
		boxShadow: theme.mode === "dark" ? "none" : "0 10px 24px rgba(15,23,42,0.06)"
	},
	sectionTitle: {
		fontWeight: 700,
		marginBottom: theme.spacing(0.5)
	},
	sectionDescription: {
		marginBottom: theme.spacing(2),
		opacity: 0.75
	},
	avatarSection: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		height: "100%",
		minHeight: 230
	},
	switchCard: {
		padding: theme.spacing(2),
		borderRadius: 14,
		border: `1px solid ${
			theme.mode === "dark" ? "rgba(255,255,255,0.14)" : "rgba(15,23,42,0.08)"
		}`,
		backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.03)" : "#f8fafc",
		marginBottom: theme.spacing(1)
	},
	switchHeader: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between",
		gap: theme.spacing(2)
	},
	switchText: {
		flex: 1
	},
	maxWidth: {
		width: "100%"
	},
	permissionItem: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "10px 16px",
		borderRadius: 10,
		border: `1px solid ${theme.mode === "dark" ? "rgba(255,255,255,0.22)" : "#e0e0e0"}`,
		marginBottom: 4,
		transition: "all 0.2s ease",
		backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.03)" : "#fff",
		"&:hover": {
			backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "#f5f5f5",
			borderColor: theme.mode === "dark" ? "rgba(255,255,255,0.35)" : "#bdbdbd"
		}
	},
	permissionLabel: {
		fontSize: "0.9rem",
		color: theme.mode === "dark" ? theme.palette.text.primary : "#424242",
		fontWeight: 500
	}
}));

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, i18n.t("validation.tooShort"))
		.max(50, i18n.t("validation.tooLong"))
		.required(i18n.t("validation.required")),
	password: Yup.string()
		.min(5, i18n.t("validation.tooShort"))
		.max(50, i18n.t("validation.tooLong")),
	email: Yup.string()
		.email(i18n.t("validation.invalidEmail"))
		.required(i18n.t("validation.required")),
	allHistoric: Yup.string().nullable()
});

const UserModal = ({ open, onClose, userId, onImageUpdate }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		email: "",
		password: "",
		profile: "user",
		startWork: "00:00",
		endWork: "23:59",
		farewellMessage: "",
		signature: "",
		allTicket: "disable",
		allowGroup: false,
		defaultTheme: "light",
		defaultMenu: "open",
		allHistoric: "disabled",
		allUserChat: "disabled",
		userClosePendingTicket: "enabled",
		showDashboard: "disabled",
		allowRealTime: "disabled",
		allowConnections: "disabled",
		autoReplyEnabled: false,
		autoReplyMessage: "",
		serviceRatingEnabled: false,
		serviceRatingMessage: "",
		canViewAllContacts: false,
		operationalVisualAlertsEnabled: true,
		operationalVisualAlertAfterMinutes: "240",
		operationalEmailAlertsEnabled: false,
		operationalEmailAlertAfterMinutes: "5",
		operationalEmailAlertRepeatMinutes: "10",
		agentModeEnabled: false,
		agentModeAgentName: "Fabio",
		agentModeGreeting: "",
		agentModeOutOfHoursMessage: "",
		agentModeOptionPaymentLabel: "Notificar soporte de pago",
		agentModeOptionResolutionLabel: "Subir resolucion de factura electronica",
		agentModeOptionSupportLabel: "Montar ticket de soporte de un error",
		telephonyEnabled: false,
		telephonyExtension: "",
		telephonyPassword: "",
		telephonyDisplayName: "",
		telephonyCallbackNumber: "",
		telephonyAllowOutgoing: true,
		telephonyAllowIncoming: false,
		telephonyAutoConnect: false
	};

	const { user: loggedInUser } = useContext(AuthContext);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [whatsappId, setWhatsappId] = useState("");
	const [schedules, setSchedules] = useState([]);
	const { whatsApps } = useWhatsApps();
	const [profileUrl, setProfileUrl] = useState(null);
	const [tab, setTab] = useState("general");
	const [avatar, setAvatar] = useState(null);
	const [showPassword, setShowPassword] = useState(false);
	const startWorkRef = useRef();
	const endWorkRef = useRef();

	useEffect(() => {
		let isMounted = true;

		const fetchUser = async () => {
			if (!userId) return;
			try {
				const { data } = await api.get(`/users/${userId}`);

				if (!isMounted) return;

				const userData = {
					...data,
					farewellMessage: data.farewellMessage || "",
					signature: data.signature || "",
					startWork: data.startWork || "00:00",
					endWork: data.endWork || "23:59",
					defaultTheme: data.defaultTheme || "light",
					defaultMenu: data.defaultMenu || "open",
					allHistoric: data.allHistoric || "disabled",
					allUserChat: data.allUserChat || "disabled",
					userClosePendingTicket: data.userClosePendingTicket || "enabled",
					showDashboard: data.showDashboard || "disabled",
					allowRealTime: data.allowRealTime || "disabled",
					allowConnections: data.allowConnections || "disabled",
					autoReplyEnabled: !!data.autoReplyEnabled,
					autoReplyMessage: data.autoReplyMessage || "",
					serviceRatingEnabled: !!data.serviceRatingEnabled,
					serviceRatingMessage: data.serviceRatingMessage || "",
					canViewAllContacts: !!data.canViewAllContacts,
					operationalVisualAlertsEnabled: data.operationalVisualAlertsEnabled !== undefined ? !!data.operationalVisualAlertsEnabled : true,
					operationalVisualAlertAfterMinutes: String(data.operationalVisualAlertAfterMinutes || "240"),
					operationalEmailAlertsEnabled: !!data.operationalEmailAlertsEnabled,
					operationalEmailAlertAfterMinutes: String(data.operationalEmailAlertAfterMinutes || "5"),
					operationalEmailAlertRepeatMinutes: String(data.operationalEmailAlertRepeatMinutes || "10"),
					agentModeEnabled: !!data.agentModeEnabled,
					agentModeAgentName: data.agentModeAgentName || "Fabio",
					agentModeGreeting: data.agentModeGreeting || "",
					agentModeOutOfHoursMessage: data.agentModeOutOfHoursMessage || "",
					agentModeOptionPaymentLabel: data.agentModeOptionPaymentLabel || "Notificar soporte de pago",
					agentModeOptionResolutionLabel: data.agentModeOptionResolutionLabel || "Subir resolucion de factura electronica",
					agentModeOptionSupportLabel: data.agentModeOptionSupportLabel || "Montar ticket de soporte de un error",
					telephonyEnabled: !!data.telephonyEnabled,
					telephonyExtension: data.telephonyExtension || "",
					telephonyPassword: data.telephonyPassword || "",
					telephonyDisplayName: data.telephonyDisplayName || "",
					telephonyCallbackNumber: data.telephonyCallbackNumber || "",
					telephonyAllowOutgoing: data.telephonyAllowOutgoing !== undefined ? !!data.telephonyAllowOutgoing : true,
					telephonyAllowIncoming: !!data.telephonyAllowIncoming,
					telephonyAutoConnect: !!data.telephonyAutoConnect
				};

				setUser(prevState => ({ ...prevState, ...userData }));

				const { profileImage } = data;
				setProfileUrl(
					profileImage
						? `${backendUrl}/public/company${data.companyId}/user/${profileImage}`
						: null
				);

				const userQueueIds = Array.isArray(data.queues)
					? data.queues.map(queue => queue.id)
					: [];
				setSelectedQueueIds(userQueueIds);
				setWhatsappId(data.whatsappId ?? "");
				setSchedules(Array.isArray(data.schedules) ? data.schedules : []);
			} catch (err) {
				if (isMounted) {
					toastError(err);
				}
			}
		};

		fetchUser();

		return () => {
			isMounted = false;
		};
	}, [userId, open]);

	const handleClose = () => {
		onClose();
		setUser(initialState);
		setSelectedQueueIds([]);
		setWhatsappId("");
		setSchedules([]);
		setTab("general");
	};

	const handleTabChange = (event, newValue) => {
		setTab(newValue);
	};

	const handleSaveUser = async values => {
		const uploadAvatar = async file => {
			try {
				const formData = new FormData();
				formData.append("userId", file.id);
				formData.append("typeArch", "user");
				formData.append("profileImage", avatar);

				const { data } = await api.post(`/users/${file.id}/media-upload`, formData);
				localStorage.setItem("profileImage", data.user.profileImage);
				setUser(prevState => ({
					...prevState,
					profileImage: data.user.profileImage
				}));
				const nextProfileUrl = data.user.profileImage
					? `${backendUrl}/public/company${data.user.companyId || file.companyId || loggedInUser.companyId}/user/${data.user.profileImage}?v=${Date.now()}`
					: null;
				setProfileUrl(nextProfileUrl);
				if (typeof onImageUpdate === "function") {
					onImageUpdate(nextProfileUrl);
				}
			} catch (err) {
				toastError(err);
			}
		};

		const userData = {
			...values,
			whatsappId,
			queueIds: selectedQueueIds,
			schedules
		};

		try {
			if (userId) {
				const { data } = await api.put(`/users/${userId}`, userData);

				if (avatar && (!user?.profileImage || user?.profileImage !== avatar.name)) {
					await uploadAvatar(data);
				}
			} else {
				const { data } = await api.post("/users", userData);

				if (avatar) {
					await uploadAvatar(data);
				}
			}

			handleClose();
			toast.success(i18n.t("userModal.success"));

			if (userId === loggedInUser.id) {
				window.location.reload();
			}
		} catch (err) {
			toastError(err);
		}
	};

	const renderProfileField = () => (
		<FormControl variant="outlined" margin="dense" fullWidth>
			<Can
				role={loggedInUser.profile}
				perform="user-modal:editProfile"
				yes={() => (
					<>
						<InputLabel id="profile-selection-input-label">
							{i18n.t("userModal.form.profile")}
						</InputLabel>
						<Field
							as={Select}
							label={i18n.t("userModal.form.profile")}
							name="profile"
							labelId="profile-selection-label"
							id="profile-selection"
							required
						>
							<MenuItem value="admin">{i18n.t("users.profile.admin")}</MenuItem>
							<MenuItem value="user">{i18n.t("users.profile.user")}</MenuItem>
						</Field>
					</>
				)}
			/>
		</FormControl>
	);

	const renderWhatsappField = () => (
		<Can
			role={loggedInUser.profile}
			perform="user-modal:editProfile"
			yes={() => (
				<FormControl variant="outlined" margin="dense" className={classes.maxWidth} fullWidth>
					<InputLabel>{i18n.t("userModal.form.whatsapp")}</InputLabel>
					<Field
						as={Select}
						value={whatsappId}
						onChange={e => setWhatsappId(e.target.value)}
						label={i18n.t("userModal.form.whatsapp")}
					>
						<MenuItem value="">&nbsp;</MenuItem>
						{whatsApps.map(whatsapp => (
							<MenuItem key={whatsapp.id} value={whatsapp.id}>
								{whatsapp.name}
							</MenuItem>
						))}
					</Field>
				</FormControl>
			)}
		/>
	);

	const renderWorkHours = touched => errors => (
		<Can
			role={loggedInUser.profile}
			perform="user-modal:editProfile"
			yes={() => (
				<Grid container spacing={1}>
					<Grid item xs={12} md={6}>
						<Field
							as={TextField}
							label={i18n.t("userModal.form.startWork")}
							type="time"
							ampm={"false"}
							inputRef={startWorkRef}
							InputLabelProps={{ shrink: true }}
							inputProps={{ step: 600 }}
							fullWidth
							name="startWork"
							error={touched.startWork && Boolean(errors.startWork)}
							helperText={touched.startWork && errors.startWork}
							variant="outlined"
							margin="dense"
							className={classes.textField}
						/>
					</Grid>
					<Grid item xs={12} md={6}>
						<Field
							as={TextField}
							label={i18n.t("userModal.form.endWork")}
							type="time"
							ampm={"false"}
							inputRef={endWorkRef}
							InputLabelProps={{ shrink: true }}
							inputProps={{ step: 600 }}
							fullWidth
							name="endWork"
							error={touched.endWork && Boolean(errors.endWork)}
							helperText={touched.endWork && errors.endWork}
							variant="outlined"
							margin="dense"
							className={classes.textField}
						/>
					</Grid>
				</Grid>
			)}
		/>
	);

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="md"
				fullWidth
				scroll="paper"
				PaperProps={{ className: classes.dialogPaper }}
			>
				<DialogTitle id="form-dialog-title">
					{userId ? i18n.t("userModal.title.edit") : i18n.t("userModal.title.add")}
				</DialogTitle>

				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveUser(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, setFieldValue, values }) => (
						<Form>
							<Paper className={classes.mainPaper} elevation={1}>
								<Tabs
									value={tab}
									indicatorColor="primary"
									textColor="primary"
									scrollButtons="on"
									variant="scrollable"
									onChange={handleTabChange}
									className={classes.tab}
								>
									<Tab label={i18n.t("userModal.tabs.general")} value="general" />
									<Tab label="Automatizacion" value="automation" />
									<Tab label={i18n.t("userModal.tabs.permissions")} value="permissions" />
									<Tab label="Disponibilidad" value="availability" />
								</Tabs>
							</Paper>

							<Paper className={classes.paper} elevation={0}>
								<DialogContent dividers className={classes.dialogContent}>
									<TabPanel className={classes.container} value={tab} name="general">
										<Grid container spacing={2}>
											<Grid item xs={12} md={4}>
												<Paper className={classes.sectionCard} elevation={0}>
													<div className={classes.avatarSection}>
														<Typography variant="subtitle1" className={classes.sectionTitle}>
															Avatar del usuario
														</Typography>
														<Typography variant="body2" className={classes.sectionDescription}>
															Suba el logo o la imagen que vera el equipo.
														</Typography>
														<div className={classes.updateDiv}>
															<AvatarUploader
																setAvatar={setAvatar}
																avatar={user.profileImage}
																avatarUrl={profileUrl}
																companyId={user.companyId || loggedInUser.companyId}
															/>
															{user.profileImage && (
																<Button
																	variant="outlined"
																	color="secondary"
																	onClick={() => {
																		user.profileImage = null;
																		setFieldValue("profileImage", null);
																		setAvatar(null);
																	}}
																>
																	{i18n.t("userModal.title.removeImage")}
																</Button>
															)}
														</div>
													</div>
												</Paper>
											</Grid>

											<Grid item xs={12} md={8}>
												<Grid container spacing={2}>
													<Grid item xs={12}>
														<Paper className={classes.sectionCard} elevation={0}>
															<Typography variant="subtitle1" className={classes.sectionTitle}>
																Identidad
															</Typography>
															<Typography variant="body2" className={classes.sectionDescription}>
																Datos principales del usuario y acceso al sistema.
															</Typography>
															<Grid container spacing={1}>
																<Grid item xs={12} md={6}>
																	<Field
																		as={TextField}
																		label={i18n.t("userModal.form.name")}
																		autoFocus
																		name="name"
																		error={touched.name && Boolean(errors.name)}
																		helperText={touched.name && errors.name}
																		variant="outlined"
																		margin="dense"
																		fullWidth
																	/>
																</Grid>
																<Grid item xs={12} md={6}>
																	<Field
																		as={TextField}
																		label={i18n.t("userModal.form.password")}
																		type={showPassword ? "text" : "password"}
																		name="password"
																		error={touched.password && Boolean(errors.password)}
																		helperText={touched.password && errors.password}
																		variant="outlined"
																		margin="dense"
																		fullWidth
																		InputProps={{
																			endAdornment: (
																				<InputAdornment position="end">
																					<IconButton
																						aria-label="toggle password visibility"
																						onClick={() => setShowPassword(!showPassword)}
																						onMouseDown={e => e.preventDefault()}
																						edge="end"
																					>
																						{showPassword ? <VisibilityOff /> : <Visibility />}
																					</IconButton>
																				</InputAdornment>
																			)
																		}}
																	/>
																</Grid>
																<Grid item xs={12} md={8}>
																	<Field
																		as={TextField}
																		label={i18n.t("userModal.form.email")}
																		name="email"
																		error={touched.email && Boolean(errors.email)}
																		helperText={touched.email && errors.email}
																		variant="outlined"
																		margin="dense"
																		fullWidth
																	/>
																</Grid>
																<Grid item xs={12} md={4}>
																	{renderProfileField()}
																</Grid>
															</Grid>
														</Paper>
													</Grid>

													<Grid item xs={12}>
														<Paper className={classes.sectionCard} elevation={0}>
															<Typography variant="subtitle1" className={classes.sectionTitle}>
																Asignacion y preferencia
															</Typography>
															<Typography variant="body2" className={classes.sectionDescription}>
																Colas del usuario, conexion asignada y apariencia inicial.
															</Typography>
															<Grid container spacing={1}>
																<Grid item xs={12}>
																	<Can
																		role={loggedInUser.profile}
																		perform="user-modal:editQueues"
																		yes={() => (
																			<QueueSelect
																				selectedQueueIds={selectedQueueIds}
																				onChange={queueValues => setSelectedQueueIds(queueValues)}
																				fullWidth
																			/>
																		)}
																	/>
																</Grid>
																<Grid item xs={12}>
																	{renderWhatsappField()}
																</Grid>
																<Grid item xs={12} md={6}>
																	<FormControl variant="outlined" className={classes.maxWidth} margin="dense" fullWidth>
																		<InputLabel>{i18n.t("userModal.form.defaultTheme")}</InputLabel>
																		<Field
																			as={Select}
																			label={i18n.t("userModal.form.defaultTheme")}
																			name="defaultTheme"
																			type="defaultTheme"
																			required
																		>
																			<MenuItem value="light">{i18n.t("userModal.form.defaultThemeLight")}</MenuItem>
																			<MenuItem value="dark">{i18n.t("userModal.form.defaultThemeDark")}</MenuItem>
																		</Field>
																	</FormControl>
																</Grid>
																<Grid item xs={12} md={6}>
																	<FormControl variant="outlined" className={classes.maxWidth} margin="dense" fullWidth>
																		<InputLabel>{i18n.t("userModal.form.defaultMenu")}</InputLabel>
																		<Field
																			as={Select}
																			label={i18n.t("userModal.form.defaultMenu")}
																			name="defaultMenu"
																			type="defaultMenu"
																			required
																		>
																			<MenuItem value="open">{i18n.t("userModal.form.defaultMenuOpen")}</MenuItem>
																			<MenuItem value="closed">{i18n.t("userModal.form.defaultMenuClosed")}</MenuItem>
																		</Field>
																	</FormControl>
																</Grid>
															</Grid>
														</Paper>
													</Grid>
												</Grid>
											</Grid>
										</Grid>
									</TabPanel>

									<TabPanel className={classes.container} value={tab} name="automation">
										<Grid container spacing={2}>
											<Grid item xs={12} md={6}>
												<Paper className={classes.sectionCard} elevation={0}>
													<Typography variant="subtitle1" className={classes.sectionTitle}>
														Mensajeria automatica
													</Typography>
													<Typography variant="body2" className={classes.sectionDescription}>
														Respuestas operativas del usuario sin activar el agente IA.
													</Typography>

													<div className={classes.switchCard}>
														<div className={classes.switchHeader}>
															<div className={classes.switchText}>
																<Typography variant="subtitle2">
																	Respuesta automatica por usuario
																</Typography>
																<Typography variant="body2" color="textSecondary">
																	Se envia cuando le escriben y esta opcion esta activa.
																</Typography>
															</div>
															<Field name="autoReplyEnabled">
																{({ field, form }) => (
																	<GreenSwitch
																		checked={!!field.value}
																		onChange={e =>
																			form.setFieldValue("autoReplyEnabled", e.target.checked)
																		}
																	/>
																)}
															</Field>
														</div>
													</div>

													<Field
														as={TextField}
														label="Mensaje de respuesta automatica"
														type="text"
														multiline
														minRows={4}
														fullWidth
														name="autoReplyMessage"
														disabled={!values.autoReplyEnabled}
														error={touched.autoReplyMessage && Boolean(errors.autoReplyMessage)}
														helperText={
															(touched.autoReplyMessage && errors.autoReplyMessage) ||
															"Tiene proteccion para no repetir el mismo mensaje a cada rato."
														}
														variant="outlined"
														margin="dense"
													/>

													<Field
														as={TextField}
														label={i18n.t("userModal.form.farewellMessage")}
														type="farewellMessage"
														multiline
														minRows={4}
														fullWidth
														name="farewellMessage"
														error={touched.farewellMessage && Boolean(errors.farewellMessage)}
														helperText={
															(touched.farewellMessage && errors.farewellMessage) ||
															"Se usa al cerrar o finalizar la atencion, segun la configuracion del sistema."
														}
														variant="outlined"
														margin="dense"
													/>

													<div className={classes.switchCard}>
														<div className={classes.switchHeader}>
															<div className={classes.switchText}>
																<Typography variant="subtitle2">
																	Calificacion por enlace al cerrar chat
																</Typography>
																<Typography variant="body2" color="textSecondary">
																	Envia un enlace publico al cliente para calificar la atencion de este usuario.
																</Typography>
															</div>
															<Field name="serviceRatingEnabled">
																{({ field, form }) => (
																	<GreenSwitch
																		checked={!!field.value}
																		onChange={e =>
																			form.setFieldValue("serviceRatingEnabled", e.target.checked)
																		}
																	/>
																)}
															</Field>
														</div>
													</div>

													<Field
														as={TextField}
														label="Mensaje de calificacion"
														type="text"
														multiline
														minRows={4}
														fullWidth
														name="serviceRatingMessage"
														disabled={!values.serviceRatingEnabled}
														helperText="Puede usar {{link}} para decidir en que parte del mensaje se inserta el enlace."
														variant="outlined"
														margin="dense"
													/>

													<div className={classes.switchCard} style={{ marginTop: 16 }}>
														<div className={classes.switchHeader}>
															<div className={classes.switchText}>
																<Typography variant="subtitle2">
																	Modo agente asistido
																</Typography>
																<Typography variant="body2" color="textSecondary">
																	Prepara a este usuario para derivar conversaciones a un agente operativo como Fabio.
																</Typography>
															</div>
															<Field name="agentModeEnabled">
																{({ field, form }) => (
																	<GreenSwitch
																		checked={!!field.value}
																		onChange={e =>
																			form.setFieldValue("agentModeEnabled", e.target.checked)
																		}
																	/>
																)}
															</Field>
														</div>
													</div>

													<Grid container spacing={2}>
														<Grid item xs={12} md={6}>
															<Field
																as={TextField}
																label="Nombre del agente"
																type="text"
																fullWidth
																name="agentModeAgentName"
																disabled={!values.agentModeEnabled}
																variant="outlined"
																margin="dense"
																helperText="Ejemplo: Fabio."
															/>
														</Grid>
													</Grid>

													<Field
														as={TextField}
														label="Mensaje base del agente"
														type="text"
														multiline
														minRows={4}
														fullWidth
														name="agentModeGreeting"
														disabled={!values.agentModeEnabled}
														helperText="Texto que presentara al agente y sus capacidades cuando este modo este activo."
														variant="outlined"
														margin="dense"
													/>

													<Field
														as={TextField}
														label="Mensaje del agente fuera de horario"
														type="text"
														multiline
														minRows={4}
														fullWidth
														name="agentModeOutOfHoursMessage"
														disabled={!values.agentModeEnabled}
														helperText="Se usara despues del mensaje normal de fuera de horario cuando luego Fabio deba ofrecer ayuda automatica."
														variant="outlined"
														margin="dense"
													/>

													<Grid container spacing={2}>
														<Grid item xs={12}>
															<Typography variant="subtitle2" style={{ marginTop: 8 }}>
																Opciones iniciales del agente
															</Typography>
															<Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
																Estas etiquetas dejan preparada la primera version del menu 1, 2 y 3.
															</Typography>
														</Grid>
														<Grid item xs={12} md={4}>
															<Field
																as={TextField}
																label="Opcion 1"
																type="text"
																fullWidth
																name="agentModeOptionPaymentLabel"
																disabled={!values.agentModeEnabled}
																variant="outlined"
																margin="dense"
															/>
														</Grid>
														<Grid item xs={12} md={4}>
															<Field
																as={TextField}
																label="Opcion 2"
																type="text"
																fullWidth
																name="agentModeOptionResolutionLabel"
																disabled={!values.agentModeEnabled}
																variant="outlined"
																margin="dense"
															/>
														</Grid>
														<Grid item xs={12} md={4}>
															<Field
																as={TextField}
																label="Opcion 3"
																type="text"
																fullWidth
																name="agentModeOptionSupportLabel"
																disabled={!values.agentModeEnabled}
																variant="outlined"
																margin="dense"
															/>
														</Grid>
													</Grid>

												</Paper>
											</Grid>

											<Grid item xs={12} md={6}>
												<Paper className={classes.sectionCard} elevation={0}>
													<Typography variant="subtitle1" className={classes.sectionTitle}>
														Firma y horario base
													</Typography>
													<Typography variant="body2" className={classes.sectionDescription}>
														Firma visible en el chat y jornada base del operador.
													</Typography>

													{renderWorkHours(touched)(errors)}

													<Field
														as={TextField}
														label="Firma del chat"
														type="signature"
														multiline
														minRows={3}
														fullWidth
														name="signature"
														error={touched.signature && Boolean(errors.signature)}
														helperText={
															(touched.signature && errors.signature) ||
															"Se usa cuando la firma del operador esta activada en el chat."
														}
														variant="outlined"
														margin="dense"
													/>
												</Paper>
											</Grid>

											<Grid item xs={12}>
												<Paper className={classes.sectionCard} elevation={0}>
													<Typography variant="subtitle1" className={classes.sectionTitle}>
														Alertas operativas del usuario
													</Typography>
													<Typography variant="body2" className={classes.sectionDescription}>
														Defina si este usuario ve pop-ups operativos y si recibe correos cuando deja clientes esperando respuesta.
													</Typography>

													<div className={classes.switchCard}>
														<div className={classes.switchHeader}>
															<div className={classes.switchText}>
																<Typography variant="subtitle2">
																	Pop-up operativo en pantalla
																</Typography>
																<Typography variant="body2" color="textSecondary">
																	Trabaja junto con el intervalo global de la empresa para recordar tickets abiertos y contactos sin nombre.
																</Typography>
															</div>
															<Field name="operationalVisualAlertsEnabled">
																{({ field, form }) => (
																	<GreenSwitch
																		checked={!!field.value}
																		onChange={e =>
																			form.setFieldValue("operationalVisualAlertsEnabled", e.target.checked)
																		}
																	/>
																)}
															</Field>
														</div>
													</div>

													<Grid container spacing={2}>
														<Grid item xs={12} md={4}>
															<Field
																as={TextField}
																label="Umbral visual (minutos)"
																type="number"
																fullWidth
																name="operationalVisualAlertAfterMinutes"
																disabled={!values.operationalVisualAlertsEnabled}
																variant="outlined"
																margin="dense"
																inputProps={{ min: 5, step: 5 }}
																helperText="Tiempo sin movimiento para que el ticket entre a la alerta operativa."
															/>
														</Grid>
													</Grid>

													<div className={classes.switchCard} style={{ marginTop: 16 }}>
														<div className={classes.switchHeader}>
															<div className={classes.switchText}>
																<Typography variant="subtitle2">
																	Correos por demora de respuesta
																</Typography>
																<Typography variant="body2" color="textSecondary">
																	Envía recordatorios automáticos al correo del usuario cuando el cliente sigue esperando respuesta humana.
																</Typography>
															</div>
															<Field name="operationalEmailAlertsEnabled">
																{({ field, form }) => (
																	<GreenSwitch
																		checked={!!field.value}
																		onChange={e =>
																			form.setFieldValue("operationalEmailAlertsEnabled", e.target.checked)
																		}
																	/>
																)}
															</Field>
														</div>
													</div>

													<Grid container spacing={2}>
														<Grid item xs={12} md={4}>
															<Field
																as={TextField}
																label="Primer aviso (minutos)"
																type="number"
																fullWidth
																name="operationalEmailAlertAfterMinutes"
																disabled={!values.operationalEmailAlertsEnabled}
																variant="outlined"
																margin="dense"
																inputProps={{ min: 1, step: 1 }}
																helperText="Primer recordatorio cuando el cliente supera este tiempo esperando."
															/>
														</Grid>
														<Grid item xs={12} md={4}>
															<Field
																as={TextField}
																label="Repetir aviso cada (minutos)"
																type="number"
																fullWidth
																name="operationalEmailAlertRepeatMinutes"
																disabled={!values.operationalEmailAlertsEnabled}
																variant="outlined"
																margin="dense"
																inputProps={{ min: 5, step: 5 }}
																helperText="Controla cada cuánto se puede reenviar otro correo del mismo ticket."
															/>
														</Grid>
														<Grid item xs={12} md={4}>
															<TextField
																label="Correo destino"
																fullWidth
																variant="outlined"
																margin="dense"
																value={values.email || ""}
																disabled
																helperText="Se usa el correo principal del usuario."
															/>
														</Grid>
													</Grid>
												</Paper>
											</Grid>
										</Grid>
									</TabPanel>

									<TabPanel className={classes.container} value={tab} name="permissions">
										<Can
											role={loggedInUser.profile}
											perform="user-modal:editProfile"
											yes={() => (
												<Grid container spacing={2}>
													<Grid item xs={12}>
														<Grid container spacing={1}>
															{[
																{ name: "canViewAllContacts", label: i18n.t("userModal.form.canViewAllContacts"), isBoolean: true },
																{ name: "allTicket", label: i18n.t("userModal.form.allTicket"), onValue: "enable", offValue: "disable" },
																{ name: "allowGroup", label: i18n.t("userModal.form.allowGroup"), isBoolean: true },
																{ name: "allHistoric", label: i18n.t("userModal.form.allHistoric"), onValue: "enabled", offValue: "disabled" },
																{ name: "allUserChat", label: i18n.t("userModal.form.allUserChat"), onValue: "enabled", offValue: "disabled" },
																{ name: "userClosePendingTicket", label: i18n.t("userModal.form.userClosePendingTicket"), onValue: "enabled", offValue: "disabled" },
																{ name: "allowConnections", label: i18n.t("userModal.form.allowConnections"), onValue: "enabled", offValue: "disabled" },
																{ name: "showDashboard", label: i18n.t("userModal.form.showDashboard"), onValue: "enabled", offValue: "disabled" },
																{ name: "allowRealTime", label: i18n.t("userModal.form.allowRealTime"), onValue: "enabled", offValue: "disabled" }
															].map(perm => (
																<Grid item xs={12} md={6} key={perm.name}>
																	<Field name={perm.name}>
																		{({ field, form }) => {
																			const isOn = perm.isBoolean
																				? !!field.value
																				: field.value === perm.onValue;
																			return (
																				<div className={classes.permissionItem}>
																					<span className={classes.permissionLabel}>{perm.label}</span>
																					<GreenSwitch
																						checked={isOn}
																						onChange={e => {
																							const val = perm.isBoolean
																								? e.target.checked
																								: e.target.checked
																									? perm.onValue
																									: perm.offValue;
																							form.setFieldValue(perm.name, val);
																						}}
																					/>
																				</div>
																			);
																		}}
																	</Field>
																</Grid>
															))}
														</Grid>
													</Grid>

													<Grid item xs={12}>
														<Paper className={classes.sectionCard} elevation={0}>
															<Typography variant="subtitle1" className={classes.sectionTitle}>
																Telefonia del usuario
															</Typography>
															<Typography variant="body2" className={classes.sectionDescription}>
																Defina si este usuario puede usar llamadas, con que extension se autentica y en que numero recibira las llamadas desde movil.
															</Typography>

															<div className={classes.switchCard}>
																<div className={classes.switchHeader}>
																	<div className={classes.switchText}>
																		<Typography variant="subtitle2">
																			Habilitar llamadas para este usuario
																		</Typography>
																		<Typography variant="body2" color="textSecondary">
																			Si esta apagado, no se mostrara el boton de llamada para este operador.
																		</Typography>
																	</div>
																	<Field name="telephonyEnabled">
																		{({ field, form }) => (
																			<GreenSwitch
																				checked={!!field.value}
																				onChange={e => form.setFieldValue("telephonyEnabled", e.target.checked)}
																			/>
																		)}
																	</Field>
																</div>
															</div>

															<Grid container spacing={2}>
																<Grid item xs={12} md={4}>
																	<Field
																		as={TextField}
																		label="Extension SIP"
																		fullWidth
																		name="telephonyExtension"
																		disabled={!values.telephonyEnabled}
																		variant="outlined"
																		margin="dense"
																	/>
																</Grid>
																<Grid item xs={12} md={4}>
																	<Field
																		as={TextField}
																		label="Contrasena SIP"
																		type="password"
																		fullWidth
																		name="telephonyPassword"
																		disabled={!values.telephonyEnabled}
																		variant="outlined"
																		margin="dense"
																	/>
																</Grid>
																<Grid item xs={12} md={4}>
																	<Field
																		as={TextField}
																		label="Nombre en pantalla"
																		fullWidth
																		name="telephonyDisplayName"
																		disabled={!values.telephonyEnabled}
																		variant="outlined"
																		margin="dense"
																	/>
																</Grid>
																<Grid item xs={12}>
																	<Field
																		as={TextField}
																		label="Numero donde el operador recibira la llamada"
																		fullWidth
																		name="telephonyCallbackNumber"
																		disabled={!values.telephonyEnabled}
																		variant="outlined"
																		margin="dense"
																		helperText="Por defecto, en movil la llamada sonara primero en este numero y luego se conectara con el cliente."
																	/>
																</Grid>
															</Grid>

															<Grid container spacing={1}>
																{[
																	{ name: "telephonyAllowOutgoing", label: "Permitir llamadas salientes" },
																	{ name: "telephonyAllowIncoming", label: "Permitir llamadas entrantes" },
																	{ name: "telephonyAutoConnect", label: "Auto abrir webphone" }
																].map(item => (
																	<Grid item xs={12} md={4} key={item.name}>
																		<Field name={item.name}>
																			{({ field, form }) => (
																				<div className={classes.permissionItem}>
																					<span className={classes.permissionLabel}>{item.label}</span>
																					<GreenSwitch
																						checked={!!field.value}
																						disabled={!values.telephonyEnabled}
																						onChange={e => form.setFieldValue(item.name, e.target.checked)}
																					/>
																				</div>
																			)}
																		</Field>
																	</Grid>
																))}
															</Grid>
														</Paper>
													</Grid>
												</Grid>
											)}
										/>
									</TabPanel>

									<TabPanel className={classes.container} value={tab} name="availability">
										<Typography variant="body2" style={{ marginBottom: 12, opacity: 0.8 }}>
											Defina la disponibilidad de este usuario por dia y, si lo necesita,
											limite cada franja por rango de fechas.
										</Typography>
										<SchedulesForm
											loading={false}
											onSubmit={scheduleValues => setSchedules(scheduleValues)}
											initialValues={schedules}
											labelSaveButton="Aplicar disponibilidad"
											embedded
										/>
									</TabPanel>
								</DialogContent>
							</Paper>

							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{userId ? i18n.t("userModal.buttons.okEdit") : i18n.t("userModal.buttons.okAdd")}
									{isSubmitting && (
										<CircularProgress size={24} className={classes.buttonProgress} />
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default UserModal;
